import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Envelope,
  Phone,
  MapPin,
  PaperPlaneRight,
  CheckCircle,
  SpinnerGap,
  InstagramLogo,
  FacebookLogo,
  LinkedinLogo,
  WhatsappLogo,
  Handshake,
  Briefcase,
  Storefront
} from '@phosphor-icons/react';
import { publicApi } from '../services/api';

const WHATSAPP = '917350695792';

const enquiryWhatsAppHref = (enquiry) => {
  if (!enquiry) return `https://wa.me/${WHATSAPP}`;
  const lines = [
    'Hi Growmore IT Services,',
    '',
    'I just sent an enquiry on the website. Please follow up.',
    '',
    `Name: ${enquiry.name || '—'}`,
    `Phone: ${enquiry.phone || '—'}`,
    `Email: ${enquiry.email || '—'}`
  ];
  if (enquiry.service) lines.push(`Regarding: ${enquiry.service}`);
  lines.push('', `Message: ${enquiry.message || '—'}`);
  return `https://wa.me/${WHATSAPP}?text=${encodeURIComponent(lines.join('\n'))}`;
};

const socials = [
  { icon: InstagramLogo, href: 'https://www.instagram.com/growmore_it_services?igsh=aG8yd2oyM2w0bGl4', label: 'Instagram', tone: 'social-ig' },
  { icon: FacebookLogo, href: 'https://www.facebook.com/GrowmoreITServices', label: 'Facebook', tone: 'social-fb' },
  { icon: LinkedinLogo, href: 'https://www.linkedin.com/company/growmore-it-services/posts/?feedView=all', label: 'LinkedIn', tone: 'social-li' }
];

const Contact = () => {
    const location = useLocation();
    const isContactPage = location.pathname === '/contact';
    const [status, setStatus] = useState('idle');
    const [error, setError] = useState('');
    const [lastEnquiry, setLastEnquiry] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        message: ''
    });

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setStatus('loading');
        setError('');

        try {
            await publicApi.post('/enquiries', {
                ...formData,
                service_slug: 'general',
                service_name: 'General enquiry',
                source_page: location.pathname || '/',
                source_label: isContactPage ? 'Contact page' : 'Home page enquiry',
                source_type: isContactPage ? 'contact_page' : 'general'
            });
            setLastEnquiry({
                ...formData,
                service: isContactPage ? 'Contact page' : 'Home page enquiry'
            });
            setStatus('success');
            setFormData({ name: '', email: '', phone: '', message: '' });
        } catch (err) {
            setStatus('idle');
            setError(err.response?.data?.message || 'Could not send enquiry. Please try again or call us.');
        }
    };

    return (
        <section id="contact" className="pt-14 md:pt-20 pb-16 md:pb-24 px-4 md:px-6 bg-white dark:bg-dark-900 border-t border-gray-100 dark:border-gray-800">
            <div className="section-max-width">
                <header className="text-center max-w-3xl mx-auto mb-6 md:mb-12">
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full badge-primary mb-3 sm:mb-5">
                        <Envelope className="w-4 h-4 sm:w-5 sm:h-5 text-brand-600 dark:text-brand-400" weight="fill" />
                        <span className="text-[11px] sm:text-sm font-semibold tracking-wide uppercase">Get in Touch</span>
                    </div>
                    <h2 className="text-display text-3xl md:text-5xl font-bold mb-3 md:mb-4 text-gray-900 dark:text-white leading-tight">
                        Let's Build Something
                        <span className="text-gradient"> Amazing Together</span>
                    </h2>
                    <p className="text-body text-sm sm:text-lg leading-relaxed">
                        Have a project in mind? We'd love to hear from you. Send us a message and we'll respond as soon as possible.
                    </p>
                </header>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-8 items-stretch">
                    <div className="h-full flex flex-col rounded-2xl sm:rounded-3xl bg-gray-50 dark:bg-dark-800 border border-gray-100 dark:border-gray-700 p-4 sm:p-6 md:p-8">
                        <div className="grid grid-cols-1 gap-3">
                            <div className="contact-tile group flex items-start gap-3 sm:gap-4 rounded-2xl bg-white dark:bg-dark-700/60 border border-gray-100 dark:border-gray-600 p-3.5 sm:p-4">
                                <span className="contact-tile-icon w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-brand-50 dark:bg-dark-800 border border-gray-100 dark:border-gray-600 text-brand-600 flex items-center justify-center shrink-0 shadow-sm smooth-transition">
                                    <Phone className="w-5 h-5" weight="fill" />
                                </span>
                                <div className="min-w-0 flex-1">
                                    <p className="font-sans text-sm sm:text-base font-bold text-gray-900 dark:text-white leading-none">Call us</p>
                                    <p className="font-sans text-xs sm:text-[15px] text-gray-500 dark:text-gray-400 mt-1.5 mb-2.5">Mon–Fri, 9am to 6pm</p>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                        <a href="tel:+917350695792" className="contact-chip">
                                            +91 73506 95792
                                        </a>
                                        <a href="tel:+919022487236" className="contact-chip">
                                            +91 90224 87236
                                        </a>
                                    </div>
                                </div>
                            </div>

                            <div className="contact-tile group flex items-start gap-3 sm:gap-4 rounded-2xl bg-white dark:bg-dark-700/60 border border-gray-100 dark:border-gray-600 p-3.5 sm:p-4" style={{ animationDelay: '80ms' }}>
                                <span className="contact-tile-icon w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-brand-50 dark:bg-dark-800 border border-gray-100 dark:border-gray-600 text-brand-600 flex items-center justify-center shrink-0 shadow-sm smooth-transition">
                                    <Envelope className="w-5 h-5" weight="fill" />
                                </span>
                                <div className="min-w-0 flex-1">
                                    <p className="font-sans text-sm sm:text-base font-bold text-gray-900 dark:text-white leading-none">Email us</p>
                                    <p className="font-sans text-xs sm:text-[15px] text-gray-500 dark:text-gray-400 mt-1.5 mb-2.5">We reply as soon as we can</p>
                                    <a href="mailto:info@growmoreitservices.com" className="contact-chip break-words text-[13px] sm:text-base">
                                        info@growmoreitservices.com
                                    </a>
                                </div>
                            </div>

                            <div className="contact-tile flex items-start gap-3 sm:gap-4 rounded-2xl bg-white dark:bg-dark-700/60 border border-gray-100 dark:border-gray-600 p-3.5 sm:p-4" style={{ animationDelay: '140ms' }}>
                                <span className="contact-tile-icon w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-brand-50 dark:bg-dark-800 border border-gray-100 dark:border-gray-600 text-brand-600 flex items-center justify-center shrink-0 shadow-sm smooth-transition">
                                    <MapPin className="w-5 h-5" weight="fill" />
                                </span>
                                <div className="min-w-0 flex-1">
                                    <p className="font-sans text-sm sm:text-base font-bold text-gray-900 dark:text-white leading-none mb-2.5">Visit us</p>
                                    <p className="contact-chip leading-6">
                                        Mangalmurti Apartment
                                        <span className="block font-medium text-gray-600 dark:text-gray-300">Near IT Park, Godoli, Satara</span>
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="mt-auto pt-4 flex items-center justify-center gap-3">
                            {socials.map((item) => {
                                const Icon = item.icon;
                                return (
                                    <a
                                        key={item.label}
                                        href={item.href}
                                        target="_blank"
                                        rel="noreferrer"
                                        aria-label={item.label}
                                        className={`social-brand contact-social ${item.tone}`}
                                    >
                                        <Icon className="w-6 h-6" weight="fill" />
                                    </a>
                                );
                            })}
                        </div>
                    </div>

                    <div className="h-full flex flex-col bg-gray-50 dark:bg-dark-800 rounded-2xl sm:rounded-3xl p-4 sm:p-6 md:p-8 border border-gray-100 dark:border-gray-700">
                        {status === 'success' ? (
                            <div className="flex-1 flex flex-col items-center justify-center text-center py-6">
                                <CheckCircle className="w-12 h-12 mx-auto mb-4 text-brand-600" weight="fill" />
                                <h3 className="font-sans text-2xl font-extrabold text-gray-900 dark:text-white mb-2">Enquiry sent</h3>
                                <p className="font-sans text-[15px] text-gray-600 dark:text-gray-300 mb-6">Thank you. Our team will contact you shortly.</p>
                                <a
                                    href={enquiryWhatsAppHref(lastEnquiry)}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold text-[15px] mb-4"
                                >
                                    <WhatsappLogo className="w-5 h-5" weight="fill" />
                                    Send this enquiry on WhatsApp
                                </a>
                                <button type="button" onClick={() => setStatus('idle')} className="text-[15px] font-semibold text-brand-600 dark:text-brand-400 hover:underline">
                                    Send another message
                                </button>
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit} className="space-y-5 flex-1 flex flex-col">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                                    <div>
                                        <label htmlFor="name" className="block font-sans text-[15px] font-semibold text-gray-800 dark:text-gray-200 mb-2">Name</label>
                                        <input
                                            type="text"
                                            id="name"
                                            name="name"
                                            value={formData.name}
                                            onChange={handleChange}
                                            className="w-full px-4 py-3 rounded-xl bg-white dark:bg-dark-700 border border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-brand-500 focus:border-transparent outline-none transition-all font-sans text-base text-gray-900 dark:text-white placeholder:text-gray-400"
                                            placeholder="John Doe"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <label htmlFor="phone" className="block font-sans text-[15px] font-semibold text-gray-800 dark:text-gray-200 mb-2">Phone</label>
                                        <input
                                            type="tel"
                                            id="phone"
                                            name="phone"
                                            value={formData.phone}
                                            onChange={handleChange}
                                            className="w-full px-4 py-3 rounded-xl bg-white dark:bg-dark-700 border border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-brand-500 focus:border-transparent outline-none transition-all font-sans text-base text-gray-900 dark:text-white placeholder:text-gray-400"
                                            placeholder="+91 98765 43210"
                                            required
                                        />
                                    </div>
                                </div>

                                <div>
                                    <label htmlFor="email" className="block font-sans text-[15px] font-semibold text-gray-800 dark:text-gray-200 mb-2">Email</label>
                                    <input
                                        type="email"
                                        id="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        className="w-full px-4 py-3 rounded-xl bg-white dark:bg-dark-700 border border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-brand-500 focus:border-transparent outline-none transition-all font-sans text-base text-gray-900 dark:text-white placeholder:text-gray-400"
                                        placeholder="john@example.com"
                                        required
                                    />
                                </div>

                                <div className="flex-1 flex flex-col">
                                    <label htmlFor="message" className="block font-sans text-[15px] font-semibold text-gray-800 dark:text-gray-200 mb-2">Message</label>
                                    <textarea
                                        id="message"
                                        name="message"
                                        value={formData.message}
                                        onChange={handleChange}
                                        rows="5"
                                        className="w-full flex-1 min-h-[120px] px-4 py-3 rounded-xl bg-white dark:bg-dark-700 border border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-brand-500 focus:border-transparent outline-none transition-all resize-none font-sans text-base text-gray-900 dark:text-white placeholder:text-gray-400"
                                        placeholder="Tell us about your project..."
                                        required
                                    ></textarea>
                                </div>

                                {error && <p className="text-[15px] text-red-500">{error}</p>}

                                <button
                                    type="submit"
                                    disabled={status === 'loading'}
                                    className="w-full btn-primary text-white py-3.5 rounded-xl group relative overflow-hidden flex items-center justify-center gap-2 disabled:opacity-70 mt-auto"
                                >
                                    <span className="relative z-10 flex items-center gap-2 font-sans text-[15px] font-semibold">
                                        {status === 'loading' ? <SpinnerGap className="w-5 h-5 animate-spin" /> : 'Send Message'}
                                        {status !== 'loading' && (
                                            <PaperPlaneRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                                        )}
                                    </span>
                                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
                                </button>
                            </form>
                        )}
                    </div>
                </div>

                {!isContactPage && <PartnerRegister />}
            </div>
        </section>
    );
};

const partnerTypes = [
    { id: 'referral', title: 'Referral', text: 'Higher commission on closed deals', icon: Handshake },
    { id: 'freelancer', title: 'Freelancer', text: 'Paid work on live client projects', icon: Briefcase },
    { id: 'seller', title: 'Sell with us', text: 'List your product or service', icon: Storefront }
];

const PartnerRegister = () => {
    const [status, setStatus] = useState('idle');
    const [error, setError] = useState('');
    const [form, setForm] = useState({
        name: '',
        phone: '',
        email: '',
        address: '',
        type: 'referral',
        written: ''
    });

    const onChange = (event) => {
        setForm({ ...form, [event.target.name]: event.target.value });
    };

    const onSubmit = async (event) => {
        event.preventDefault();
        setStatus('loading');
        setError('');
        const typeLabel = partnerTypes.find((item) => item.id === form.type)?.title || 'Referral';
        try {
            await publicApi.post('/enquiries', {
                name: form.name,
                phone: form.phone,
                email: form.email,
                company: form.address,
                message: `Type: ${typeLabel}\n\n${form.written}`,
                service_slug: form.type,
                service_name: typeLabel,
                source_page: '/',
                source_label: 'Partner programme',
                source_type: 'partner_register'
            });
            setStatus('success');
            setForm({ name: '', phone: '', email: '', address: '', type: 'referral', written: '' });
        } catch (err) {
            setStatus('idle');
            setError(err.response?.data?.message || 'Could not send registration. Please try again.');
        }
    };

    return (
        <div id="partner-register" className="partner-register mt-10 md:mt-14 w-full">
            <header className="text-center max-w-3xl mx-auto mb-6 md:mb-10">
                <p className="inline-flex items-center justify-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 rounded-full badge-primary mb-3 sm:mb-5 font-sans text-[11px] sm:text-sm font-semibold uppercase tracking-wide">
                    <Handshake className="w-4 h-4 sm:w-5 sm:h-5 text-brand-600 dark:text-brand-400" weight="fill" />
                    Official partner programme
                </p>
                <h3 className="font-sans text-3xl md:text-5xl font-extrabold mb-3 md:mb-5 text-gray-900 dark:text-white leading-tight tracking-tight">
                    Earn higher official commission on every client you{' '}
                    <span className="text-gradient">refer to Growmore.</span>
                </h3>
                <p className="font-sans text-sm sm:text-base md:text-lg text-gray-600 dark:text-gray-300 leading-relaxed">
                    <span className="block">
                        Join Growmore as an official partner. Refer a client and earn higher commission on every closed deal.
                    </span>
                    <span className="block mt-2">
                        Take freelance work on live projects, or sell your product through our network. Share your details — our team will contact you on your number and email.
                    </span>
                </p>
            </header>

            <div className="partner-register-card relative w-full max-w-3xl mx-auto overflow-hidden rounded-2xl sm:rounded-3xl border border-brand-100 dark:border-brand-800 p-4 sm:p-6">
                <div className="partner-register-glow" aria-hidden="true" />
                <div className="relative z-[1] space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                        {partnerTypes.map((item) => {
                            const Icon = item.icon;
                            const active = form.type === item.id;
                            return (
                                <button
                                    key={item.id}
                                    type="button"
                                    onClick={() => setForm({ ...form, type: item.id })}
                                    className={`partner-type-card text-left rounded-xl border px-3 py-3 flex items-start gap-2.5 ${
                                        active
                                            ? 'bg-white dark:bg-dark-800 border-brand-400 shadow-soft'
                                            : 'bg-white/70 dark:bg-dark-800/70 border-white/80 dark:border-gray-700'
                                    }`}
                                >
                                    <span className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${
                                        active ? 'bg-brand-50 text-brand-600' : 'bg-gray-100 dark:bg-dark-700 text-gray-500'
                                    }`}>
                                        <Icon className="w-4 h-4" weight="fill" />
                                    </span>
                                    <span>
                                        <span className="block text-sm font-semibold text-gray-900 dark:text-white leading-tight">{item.title}</span>
                                        <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5 leading-snug">{item.text}</span>
                                    </span>
                                </button>
                            );
                        })}
                    </div>

                    {status === 'success' ? (
                        <div className="rounded-xl bg-white dark:bg-dark-800 border border-gray-100 dark:border-gray-700 px-5 py-6 text-center shadow-soft">
                            <CheckCircle className="w-8 h-8 mx-auto text-brand-600 dark:text-brand-400" weight="fill" />
                            <p className="font-display text-lg font-bold text-gray-900 dark:text-white mt-2">Partner request received</p>
                            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Our team will call or email you about the next step.</p>
                            <button type="button" onClick={() => setStatus('idle')} className="mt-3 text-sm font-semibold text-brand-600 dark:text-brand-400">Send another request</button>
                        </div>
                    ) : (
                        <form onSubmit={onSubmit} className="rounded-xl bg-white/90 dark:bg-dark-800/90 border border-white/80 dark:border-gray-700 shadow-soft p-3 sm:p-4 grid sm:grid-cols-2 gap-2.5">
                            <label className="block text-xs font-semibold text-gray-800 dark:text-gray-200">
                                Full name
                                <input name="name" value={form.name} onChange={onChange} required className="input-field mt-1 !py-2" placeholder="Your full name" />
                            </label>
                            <label className="block text-xs font-semibold text-gray-800 dark:text-gray-200">
                                Mobile number
                                <input name="phone" type="tel" value={form.phone} onChange={onChange} required className="input-field mt-1 !py-2" placeholder="10-digit mobile number" />
                            </label>
                            <label className="block text-xs font-semibold text-gray-800 dark:text-gray-200">
                                Email
                                <input name="email" type="email" value={form.email} onChange={onChange} required className="input-field mt-1 !py-2" placeholder="name@email.com" />
                            </label>
                            <label className="block text-xs font-semibold text-gray-800 dark:text-gray-200">
                                City / address
                                <input name="address" value={form.address} onChange={onChange} required className="input-field mt-1 !py-2" placeholder="City or full address" />
                            </label>
                            <label className="block text-xs font-semibold text-gray-800 dark:text-gray-200 sm:col-span-2">
                                Tell us about your work
                                <textarea name="written" value={form.written} onChange={onChange} required rows="2" className="input-field mt-1 !py-2 resize-none" placeholder="Clients you can refer, skills, or what you want to sell." />
                            </label>
                            {error && <p className="sm:col-span-2 text-sm text-red-500">{error}</p>}
                            <button type="submit" disabled={status === 'loading'} className="sm:col-span-2 btn-primary text-white inline-flex items-center justify-center gap-2 py-2.5">
                                {status === 'loading' ? <SpinnerGap className="w-5 h-5 animate-spin" /> : 'Apply as a partner'}
                            </button>
                        </form>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Contact;
